import { supabase } from '@/lib/supabase';

export type AccessLevel = 'admin' | 'editor' | 'viewer' | 'none';

export type ModulePermission = {
  id: string;
  roleId: string;
  roleName: string;
  module: string;
  access: AccessLevel;
};

export type PermissionMatrix = {
  roles: Array<{ id: string; value: string }>;
  modules: string[];
  entries: ModulePermission[];
};

export type UserPermissionRow = {
  profileId: string;
  name: string;
  email: string;
  pictureUrl: string | null;
  isOwner: boolean;
  roles: string[];
  effectiveAccess: Record<string, AccessLevel>;
};

export const MODULE_LABELS: Record<string, string> = {
  financex: 'FinanceX',
  peoplex: 'PeopleX',
  talentx: 'TalentX',
  seox: 'SEOx',
  academy: 'Academy',
  settings: 'Settings',
};

export const MODULES = Object.keys(MODULE_LABELS);

export const ACCESS_LABELS: Record<AccessLevel, string> = {
  admin: 'Admin',
  editor: 'Editor',
  viewer: 'Viewer',
  none: 'No Access',
};

const permissionsService = {
  async getMatrix(): Promise<PermissionMatrix> {
    const { data: roles, error: rolesErr } = await supabase
      .from('roles')
      .select('id, value')
      .order('value');

    if (rolesErr) throw rolesErr;

    const { data: entries, error: entriesErr } = await supabase
      .from('module_permissions')
      .select('id, role_id, module, access, roles(value)')
      .order('module');

    if (entriesErr) throw entriesErr;

    const mapped: ModulePermission[] = (entries ?? []).map((e: any) => ({
      id: e.id,
      roleId: e.role_id,
      roleName: e.roles?.value ?? '',
      module: e.module,
      access: e.access as AccessLevel,
    }));

    return {
      roles: (roles ?? []).map((r: any) => ({ id: r.id, value: r.value })),
      modules: MODULES,
      entries: mapped,
    };
  },

  async setAccess(roleId: string, module: string, access: AccessLevel): Promise<void> {
    const { error } = await supabase
      .from('module_permissions')
      .upsert(
        { role_id: roleId, module, access, updated_at: new Date().toISOString() },
        { onConflict: 'role_id,module' }
      );

    if (error) throw error;
  },

  async bulkSetAccess(
    updates: Array<{ roleId: string; module: string; access: AccessLevel }>
  ): Promise<void> {
    const rows = updates.map((u) => ({
      role_id: u.roleId,
      module: u.module,
      access: u.access,
      updated_at: new Date().toISOString(),
    }));

    const { error } = await supabase
      .from('module_permissions')
      .upsert(rows, { onConflict: 'role_id,module' });

    if (error) throw error;
  },

  getAccessForRole(
    entries: ModulePermission[],
    roleId: string,
    module: string
  ): AccessLevel {
    const entry = entries.find((e) => e.roleId === roleId && e.module === module);
    return entry?.access ?? 'none';
  },

  async getUserPermissions(
    matrixEntries: ModulePermission[]
  ): Promise<UserPermissionRow[]> {
    // Fetch users with their roles
    const { data: profiles, error } = await supabase
      .from('profiles')
      .select('id, first_name, last_name, email, picture_url, is_owner')
      .not('auth_user_id', 'is', null)
      .is('deleted_at', null)
      .order('first_name');

    if (error) throw error;

    const { data: allProfileRoles } = await supabase
      .from('profile_roles')
      .select('profile_id, role_id, roles(id, value)');

    // Build a map of profile_id → role IDs and names
    const profileRolesMap = new Map<string, Array<{ id: string; value: string }>>();
    for (const pr of allProfileRoles ?? []) {
      const p = pr as any;
      if (!profileRolesMap.has(p.profile_id)) profileRolesMap.set(p.profile_id, []);
      profileRolesMap.get(p.profile_id)?.push({ id: p.roles.id, value: p.roles.value });
    }

    const accessRank: Record<AccessLevel, number> = { admin: 4, editor: 3, viewer: 2, none: 1 };

    return (profiles ?? []).map((p: any) => {
      const userRoles = profileRolesMap.get(p.id) ?? [];
      const roleNames = userRoles.map((r) => r.value);
      const isOwner = p.is_owner === true;

      // Compute effective access per module: highest access from any of the user's roles
      const effectiveAccess: Record<string, AccessLevel> = {};
      for (const mod of MODULES) {
        if (isOwner) {
          effectiveAccess[mod] = 'admin';
          continue;
        }

        let highest: AccessLevel = 'none';
        for (const role of userRoles) {
          const roleAccess = matrixEntries.find(
            (e) => e.roleId === role.id && e.module === mod
          )?.access ?? 'none';
          if (accessRank[roleAccess] > accessRank[highest]) {
            highest = roleAccess;
          }
        }
        effectiveAccess[mod] = highest;
      }

      return {
        profileId: p.id,
        name: `${p.first_name} ${p.last_name}`.trim(),
        email: p.email,
        pictureUrl: p.picture_url,
        isOwner,
        roles: roleNames,
        effectiveAccess,
      };
    });
  },

  async setOwner(profileId: string): Promise<void> {
    // Remove owner from all profiles first
    await supabase.from('profiles').update({ is_owner: false }).eq('is_owner', true);
    // Set new owner
    const { error } = await supabase
      .from('profiles')
      .update({ is_owner: true })
      .eq('id', profileId);
    if (error) throw error;
  },

  async removeOwner(profileId: string): Promise<void> {
    const { error } = await supabase
      .from('profiles')
      .update({ is_owner: false })
      .eq('id', profileId);
    if (error) throw error;
  },
};

export default permissionsService;
